import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(email, password, name);
        toast.success("Account created successfully!");
      }
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/80b49c61-fc1e-412f-adda-b5442bedf17f/images/d46d80be4794dcf3b6bc1d578a540a525537385afd0ecdfc56483e49b2ee6d95.png')`
        }}
      >
        <div className="absolute inset-0 bg-navy/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="font-serif text-5xl mb-4">ProposalDesk</h1>
          <p className="text-xl text-white/80 mb-2">Travel Proposals</p>
          <p className="text-sm text-white/50 tracking-widest uppercase">Muskan Travel Associates</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-cream">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-12">
            <h1 className="font-serif text-4xl text-navy mb-2">ProposalDesk</h1>
            <p className="text-muted-foreground text-sm tracking-widest uppercase">Muskan Travel Associates</p>
          </div>

          <div className="bg-white border border-border p-8">
            <div className="text-center mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">
                {isLogin ? "Welcome Back" : "Get Started"}
              </p>
              <h2 className="font-serif text-2xl text-navy">
                {isLogin ? "Sign in to your account" : "Create your account"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Pratyush Sareen"
                    required={!isLogin}
                    className="bg-cream border-border focus:border-navy focus:ring-1 focus:ring-navy"
                    data-testid="register-name-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  required
                  className="bg-cream border-border focus:border-navy focus:ring-1 focus:ring-navy"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-cream border-border focus:border-navy focus:ring-1 focus:ring-navy"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-navy hover:bg-[#2a3a5e] text-white h-11"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                    {isLogin ? "Signing in..." : "Creating account..."}
                  </span>
                ) : (
                  isLogin ? "Sign In" : "Create Account"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-muted-foreground hover:text-navy transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <span className="text-gold font-medium">
                  {isLogin ? "Sign up" : "Sign in"}
                </span>
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Travel · Corporate · Cruises · Visa
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
